import { useCallback, useEffect, useRef } from "react";
import { getKeyCode, isEnterKey, isIpadDevice, isQrCodePrefixPressed, isShiftKey } from "../../helpers/browserHelper";
import { useSeller } from "../../hooks/useAccount";
import { isQrCodeEnabled } from "../../models/Seller";
import { useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { log } from "../../utils/log";

interface QrCodeScannerProps {
    onComplete?: (data: string) => void;
}

export const QrCodeScanner = ({ onComplete }: QrCodeScannerProps) => {
    const seller = useSeller();
    const isIPadDevice = isIpadDevice();
    const isScanning = useRef(false);
    const scannedData = useRef("");
    const purchaseItemsLength = useCreatePurchaseStore((state) => state.purchase.items.length);

    const handleScan = useCallback(
        (e: KeyboardEvent) => {
            const code = getKeyCode(e);

            let isQRCodePrefixPressed = false;
            if (!isScanning.current) {
                //   Given there is no scanning being done....
                if (isIPadDevice) {
                    // No need to check if QRCodePrefix is pressed in case of iPad device.
                    isQRCodePrefixPressed = true;
                } else if (isQrCodePrefixPressed(e)) {
                    log("QRCodePrefix found", {
                        origin: "QRCodeScanner.onScan",
                        code: code,
                    });

                    // QRCodePrefix is pressed i.e Ctrl + B
                    isQRCodePrefixPressed = true;

                    // Prevents Ctrl+B from calling default browser action. Ex. Bookmarks in Firefox
                    e.preventDefault();
                }
            }

            if (isQRCodePrefixPressed && !isScanning.current) {
                isScanning.current = true;
                scannedData.current = "";

                if (isIPadDevice) {
                    // Parse first char in case of Touch Devices.
                    parseScan(code);
                }
            } else if (isScanning.current) {
                parseScan(code);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [purchaseItemsLength],
    );

    const parseScan = (code: number) => {
        if (isEnterKey(code)) {
            // Enter is recieved so stop scanning.
            log("Enter key received, scan complete", {
                origin: "QRCodeScanner.parseScan",
                scannedData: scannedData,
            });
            isScanning.current = false;
            onComplete?.(scannedData.current);
            return;
        }

        if (!isShiftKey(code)) {
            // Remove shift char if it is present
            scannedData.current += String.fromCodePoint(code);
        }
    };

    useEffect(() => {
        // Event Listener for KeyDown event
        if (!isQrCodeEnabled(seller)) {
            return;
        }

        window.addEventListener("keydown", handleScan);

        return () => {
            // Cleanup
            window.removeEventListener("keydown", handleScan);
        };
    }, [handleScan, seller]);

    return null;
};
